import { useEffect, useState } from 'react'
import { useApolloClient } from '@apollo/client'
import {
  RetailerNexusLocations,
  RetailerNexusLocationsResponse,
} from '../../../../queries/calculate-tax'
import { FEATURE_ENABLED_AUTOTAX_FOR_DELIVERIES } from '../../../../constants/wr-features'
import { useRegisterManager } from '../../../../ng-compat/hooks'
import { getFeatureFromCache } from '../../../../util/feature-cache'
import { US_COUNTRY_CODE } from '../../../../vend-ui/constants'

export interface AutoTaxSettings {
  isDeliveryTaxEnabled: boolean
}

/**
 * Fetches the latest autotax settings from GraphQL.
 * TODO: version autotax setting so it is only fetched when updated
 */
export const refreshAutotaxSettings = async (
  client: ReturnType<typeof useApolloClient>
): Promise<AutoTaxSettings | undefined> => {
  try {
    const result = await client.query<RetailerNexusLocationsResponse>({
      query: RetailerNexusLocations,
      fetchPolicy: 'network-only',
    })
    return {
      isDeliveryTaxEnabled:
        result.data?.retailerNexusLocations?.isDeliveryTaxEnabled ?? false,
    }
  } catch {
    return undefined
  }
}

/**
 * Manages autotax settings (e.g. isDeliveryTaxEnabled).
 *
 * Fetches fresh from GraphQL on every mount.
 */
export const useAutotaxSettings = (): AutoTaxSettings => {
  const client = useApolloClient()
  const registerManager = useRegisterManager()
  const [isDeliveryTaxEnabled, setIsDeliveryTaxEnabled] = useState(false)
  const isAutoTaxFeatureFlagEnabled = getFeatureFromCache(
    FEATURE_ENABLED_AUTOTAX_FOR_DELIVERIES
  )
  const currentOutlet = registerManager.getActiveOutlet()
  const isUSOutlet = currentOutlet?.physical_country_id === US_COUNTRY_CODE

  useEffect(() => {
    if (!isAutoTaxFeatureFlagEnabled || !isUSOutlet) return

    let cancelled = false

    refreshAutotaxSettings(client).then(settings => {
      if (!cancelled && settings?.isDeliveryTaxEnabled !== undefined) {
        setIsDeliveryTaxEnabled(settings.isDeliveryTaxEnabled)
      }
    })

    return () => {
      cancelled = true
    }
  }, [client, isAutoTaxFeatureFlagEnabled, isUSOutlet])

  return { isDeliveryTaxEnabled }
}
